import { useMemo } from 'react';
import { LayoutGrid, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  MOBILE_ACTION_ICON,
  type MobileHeaderActionId,
  type MobileHeaderActionMeta,
} from '@/lib/mobileHeaderActions';
import {
  MIN_MOBILE_BOTTOM_NAV_ACTIONS,
  MAX_MOBILE_BOTTOM_NAV_ACTIONS,
  setMobileBottomNavActions,
  useMobileBottomNavActions,
} from '@/lib/mobileBottomNav';
import { useMobileShortcutAvailability } from '@/lib/useMobileShortcutAvailability';

export default function MobileBottomNavTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const selected = useMobileBottomNavActions(user?.id);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const { visibleCatalog, isAvailable } = useMobileShortcutAvailability();

  const toggle = (id: MobileHeaderActionId, on: boolean) => {
    if (on) {
      if (selectedSet.has(id)) return;
      if (selected.length >= MAX_MOBILE_BOTTOM_NAV_ACTIONS) {
        toast({
          title: 'Limite raggiunto',
          description: `Puoi avere al massimo ${MAX_MOBILE_BOTTOM_NAV_ACTIONS} scorciatoie nella barra inferiore (lo slot "Altro" è fisso). Disattivane una per aggiungerne un'altra.`,
          variant: 'destructive',
        });
        return;
      }
      setMobileBottomNavActions(user?.id, [...selected, id]);
    } else {
      if (!selectedSet.has(id)) return;
      if (selected.length <= MIN_MOBILE_BOTTOM_NAV_ACTIONS) {
        toast({
          title: 'Minimo richiesto',
          description: `Devono restare almeno ${MIN_MOBILE_BOTTOM_NAV_ACTIONS} scorciatoie nella barra inferiore.`,
          variant: 'destructive',
        });
        return;
      }
      setMobileBottomNavActions(user?.id, selected.filter((s) => s !== id));
    }
  };

  const renderRow = (meta: MobileHeaderActionMeta) => {
    const isOn = selectedSet.has(meta.id);
    const Icon = MOBILE_ACTION_ICON[meta.id];
    return (
      <div
        key={meta.id}
        className="flex items-center justify-between gap-4 py-3"
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
              isOn
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <Label className="text-sm font-medium block">{meta.label}</Label>
            <p className="text-xs text-muted-foreground">{meta.description}</p>
          </div>
        </div>
        <Switch
          checked={isOn}
          onCheckedChange={(checked) => toggle(meta.id, checked)}
          aria-label={`Mostra ${meta.label} nella barra mobile inferiore`}
        />
      </div>
    );
  };

  // Anteprima visiva della bottom nav. Mostriamo solo le voci ancora valide
  // più lo slot "Altro" hard-coded.
  const previewIds = selected.filter(isAvailable);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LayoutGrid className="h-5 w-5" />
          Barra mobile inferiore
        </CardTitle>
        <CardDescription>
          Scegli fino a {MAX_MOBILE_BOTTOM_NAV_ACTIONS} scorciatoie da tenere
          sempre a portata di pollice nella barra in basso (visibile solo da
          smartphone). L'ultimo tasto "Altro" apre il menu completo ed è fisso.
          Le scelte sono salvate solo su questo dispositivo e indipendenti
          dalle azioni dell'header.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Anteprima
          </p>
          <div className="rounded-2xl border bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 p-3 shadow-sm">
            <div className="flex items-stretch justify-around gap-1">
              {previewIds.map((id) => {
                const Icon = MOBILE_ACTION_ICON[id];
                const meta = visibleCatalog.find((m) => m.id === id);
                return (
                  <div
                    key={id}
                    className="flex flex-col items-center justify-center flex-1 min-w-0 py-2 rounded-lg text-gray-600 dark:text-gray-300"
                  >
                    <Icon className="h-5 w-5 mb-1" />
                    <span className="text-[10px] font-medium leading-tight truncate max-w-full px-1">
                      {meta?.label || id}
                    </span>
                  </div>
                );
              })}
              <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-2 rounded-lg text-blue-600 dark:text-blue-400">
                <MoreHorizontal className="h-5 w-5 mb-1" />
                <span className="text-[10px] font-semibold leading-tight">Altro</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {selected.length} di {MAX_MOBILE_BOTTOM_NAV_ACTIONS} scorciatoie
            selezionate. Lo slot "Altro" è sempre presente e apre il menu completo.
          </p>
        </div>

        <div className="divide-y divide-border">
          {visibleCatalog.map(renderRow)}
        </div>
      </CardContent>
    </Card>
  );
}
